import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bookmark, FileText, Briefcase, HelpCircle, Newspaper, Video, MapPin } from 'lucide-react';
import { getApiBaseUrl } from '../config/api';
import { fetchSavedPosts, setPostSaved, savedItemPath } from '../utils/saved';
import { getAuthorDisplayName } from '../utils/displayName';
import { resolveAvatarUrl, handleAvatarError } from '../utils/profilePicUrl';
import './SavedPage.css';

const API_URL = getApiBaseUrl();
const PAGE_SIZE = 20;

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'posts', label: 'Posts' },
  { id: 'articles', label: 'Articles' },
  { id: 'jobs', label: 'Jobs' },
  { id: 'questions', label: 'Q&A' },
];

const TYPE_LABELS = {
  article: { label: 'Article', Icon: Newspaper },
  blog: { label: 'Blog', Icon: Newspaper },
  hiring: { label: 'Job opening', Icon: Briefcase },
  looking_for_job: { label: 'Open to work', Icon: Briefcase },
  question: { label: 'Question', Icon: HelpCircle },
};

function formatDate(value) {
  if (!value) return '';
  const d = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** /saved — everything the member bookmarked from the feed. */
export default function SavedPage({ user }) {
  const [tab, setTab] = useState('all');
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const requestRef = useRef(0);

  const load = useCallback(
    async (offset = 0) => {
      if (!user?.id) return;
      const requestId = ++requestRef.current;
      setLoading(true);
      const data = await fetchSavedPosts(user.id, { type: tab, limit: PAGE_SIZE, offset });
      if (requestId !== requestRef.current) return;
      const incoming = data.success ? data.items || [] : [];
      setItems((prev) => (offset === 0 ? incoming : [...prev, ...incoming]));
      setHasMore(Boolean(data.has_more));
      setLoading(false);
    },
    [user?.id, tab]
  );

  useEffect(() => {
    setItems([]);
    load(0);
  }, [load]);

  const handleUnsave = async (item) => {
    setRemoving(item.id);
    const result = await setPostSaved(user.id, item.id, false);
    setRemoving(null);
    if (result.success) {
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } else {
      alert(result.message || 'Could not remove this item.');
    }
  };

  return (
    <main className="sv-page">
      <header className="sv-header">
        <h1 className="sv-title">
          <Bookmark size={22} strokeWidth={2.2} aria-hidden="true" /> Saved
        </h1>
        <p className="sv-subtitle">Only you can see what you've saved.</p>
      </header>

      <div className="sv-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`sv-tab${tab === t.id ? ' sv-tab--active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!loading && items.length === 0 ? (
        <div className="sv-empty">
          <Bookmark size={36} strokeWidth={1.6} aria-hidden="true" />
          <p className="sv-empty-title">Nothing saved here yet</p>
          <p>
            Tap the bookmark on any post, article, job, or question to keep it for later.
          </p>
          <Link to="/home" className="sv-empty-link">Browse the feed</Link>
        </div>
      ) : (
        <ul className="sv-list">
          {items.map((item) => {
            const typeMeta = TYPE_LABELS[item.post_type];
            const TypeIcon = typeMeta?.Icon || FileText;
            const path = savedItemPath(item);
            const title = item.title?.trim();
            return (
              <li key={item.id} className="sv-item">
                <Link to={path} className="sv-item-main">
                  {item.thumbnail ? (
                    <img src={item.thumbnail} alt="" className="sv-thumb" loading="lazy" />
                  ) : (
                    <span className="sv-thumb sv-thumb--icon" aria-hidden="true">
                      {item.has_video ? <Video size={22} /> : <TypeIcon size={22} />}
                    </span>
                  )}
                  <div className="sv-item-text">
                    {typeMeta && (
                      <span className="sv-type">
                        <TypeIcon size={12} strokeWidth={2.4} aria-hidden="true" /> {typeMeta.label}
                        {item.post_type === 'hiring' && item.hiring_closed && (
                          <span className="sv-closed"> · Closed</span>
                        )}
                      </span>
                    )}
                    {title && <p className="sv-item-title">{title}</p>}
                    {item.post_type === 'hiring' && (item.hiring_company || item.hiring_location) && (
                      <p className="sv-job-meta">
                        {item.hiring_company}
                        {item.hiring_location && (
                          <>
                            {' '}
                            <MapPin size={12} aria-hidden="true" /> {item.hiring_location}
                          </>
                        )}
                      </p>
                    )}
                    {item.excerpt && <p className="sv-excerpt">{item.excerpt}</p>}
                    <div className="sv-author">
                      <img
                        src={resolveAvatarUrl(item, API_URL)}
                        onError={handleAvatarError}
                        alt=""
                        className="sv-avatar"
                      />
                      <span>{getAuthorDisplayName(item)}</span>
                      <span className="sv-dot" aria-hidden="true">·</span>
                      <span>Saved {formatDate(item.saved_at)}</span>
                    </div>
                  </div>
                </Link>
                <button
                  type="button"
                  className="sv-unsave"
                  onClick={() => handleUnsave(item)}
                  disabled={removing === item.id}
                  aria-label="Remove from saved"
                  title="Remove from saved"
                >
                  <Bookmark size={18} strokeWidth={2.1} fill="currentColor" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {loading && <p className="sv-loading">Loading…</p>}
      {!loading && hasMore && (
        <button type="button" className="sv-more" onClick={() => load(items.length)}>
          Load more
        </button>
      )}
    </main>
  );
}
